/**
 * نمودارهای سربرگِ «پرچم‌های رخداد» — بی هیچ کتابخانه‌ای.
 *
 * دو شکل، و هر دو سه‌رنگِ ثابت دارند تا یک رنگ همه‌جا یک معنی بدهد:
 * باز = `--warn`، تاییدشده = `--accent`، ردشده = `--ink3`. (`--brand` در تمِ تیره همان رنگِ `--warn` است، پس برای تاییدشده به کار نمی‌آید.)
 *
 *   • `DayColumns` — ستونِ روزانه، روی هم چیده به‌ترتیبِ وضعیت.
 *   • `StackBars` — فهرستِ میله‌ای افقی (دسته، فیلد، تگ، منبع، شهر، …)، هر
 *     میله به سه تکه‌ی وضعیت، با نرخِ تایید کنارش.
 *
 * چرا SVG و div و نه یک کتابخانه‌ی نمودار: افزودنِ وابستگی اجازه می‌خواهد و
 * این‌ها فقط مستطیل‌اند. همان کاری که `daybars`ِ سربرگِ فعالیت می‌کند.
 */
import React from 'react';
import { STATUS_LABEL, type CountRow, type DayRow, type IssueStatus } from '@/core/lib/eventIssuesReport';
import { fmtNum as fmt } from '@/ui/Num';

const ORDER: IssueStatus[] = ['accepted', 'rejected', 'open'];

export const STATUS_COLOR: Record<IssueStatus, string> = {
  open: 'var(--warn)', accepted: 'var(--accent)', rejected: 'var(--ink3)',
};

export function Legend() {
  return (
    <div className="chiprow" style={{ marginTop: 6 }}>
      {(['open', 'accepted', 'rejected'] as IssueStatus[]).map((s) => (
        <span key={s} className="dim" style={{ fontSize: 12 }}>
          <span className="iss-swatch" style={{ background: STATUS_COLOR[s] }} />
          {STATUS_LABEL[s]}
        </span>
      ))}
    </div>
  );
}

const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)}٪`);

/** ستونِ روزانه. ارتفاع نسبت به بلندترین روزِ همین پنجره است. */
export function DayColumns({ rows }: { rows: DayRow[] }) {
  const peak = Math.max(1, ...rows.map((r) => r.total));
  const W = 720;
  const H = 140;
  const gap = 2;
  const w = Math.max(2, W / rows.length - gap);
  const tick = Math.max(1, Math.round(rows.length / 8));
  return (
    <div className="iss-chart">
      <svg viewBox={`0 0 ${W} ${H + 18}`} width="100%" role="img" aria-label="پرچم در روز">
        <line x1={0} x2={W} y1={H} y2={H} style={{ stroke: 'var(--line)' }} />
        {rows.map((r, i) => {
          const x = i * (W / rows.length);
          let y = H;
          return (
            <g key={r.day}>
              <title>{`${r.day}: ${r.total} — باز ${r.open} · تایید ${r.accepted} · رد ${r.rejected}`}</title>
              {/* ستونِ پهنِ نامرئی تا ماوس روی روزِ صفر هم عدد را نشان بدهد. */}
              <rect x={x} y={0} width={w} height={H} style={{ fill: 'transparent' }} />
              {ORDER.map((s) => {
                const h = (r[s] / peak) * (H - 4);
                if (h <= 0) return null;
                y -= h;
                return <rect key={s} x={x} y={y} width={w} height={h} style={{ fill: STATUS_COLOR[s] }} />;
              })}
              {i % tick === 0 ? (
                <text x={x + w / 2} y={H + 13} textAnchor="middle"
                  style={{ fill: 'var(--ink3)', fontSize: 10 }}>{r.day.slice(5)}</text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <p className="dim" style={{ fontSize: 12, margin: '2px 0 0' }}>
        بلندترین روز: {fmt(peak)} پرچم. ماوس را روی ستون نگه دار تا عددها بیایند.
      </p>
    </div>
  );
}

/** فهرستِ میله‌ای. `onPick` کلیک روی یک ردیف را به فیلترِ فهرست وصل می‌کند. */
export function StackBars({ rows, empty = 'چیزی نیست.', onPick, rate = true }: {
  rows: CountRow[];
  empty?: string;
  onPick?: (key: string) => void;
  rate?: boolean;
}) {
  if (!rows.length) return <p className="dim">{empty}</p>;
  const peak = Math.max(1, ...rows.map((r) => r.total));
  return (
    <div className="iss-bars">
      {rows.map((r) => (
        <div key={r.key} className={`iss-bar${onPick ? ' pick' : ''}`}
          onClick={onPick ? () => onPick(r.key) : undefined}
          title={`باز ${r.open} · تایید ${r.accepted} · رد ${r.rejected}`}>
          <span className="lbl">{r.label}</span>
          <span className="track">
            {ORDER.map((s) => (r[s] > 0 ? (
              <span key={s} className="seg" style={{ width: `${(r[s] / peak) * 100}%`, background: STATUS_COLOR[s] }} />
            ) : null))}
          </span>
          <span className="num">{fmt(r.total)}</span>
          {rate ? <span className="num dim" title="سهمِ تاییدشده از تصمیم‌گرفته‌ها">{pct(r.acceptRate)}</span> : null}
        </div>
      ))}
    </div>
  );
}

/** میله‌ی سهم: کلِ پرچم‌ها به سه وضعیت. */
export function ShareBar({ open, accepted, rejected }: { open: number; accepted: number; rejected: number }) {
  const total = open + accepted + rejected;
  if (!total) return null;
  return (
    <div className="sharebar" title={`باز ${open} · تایید ${accepted} · رد ${rejected}`}>
      {([['accepted', accepted], ['rejected', rejected], ['open', open]] as [IssueStatus, number][])
        .map(([s, n]) => (n > 0 ? (
          <span key={s} style={{ width: `${(n / total) * 100}%`, background: STATUS_COLOR[s] }} />
        ) : null))}
    </div>
  );
}
